import {Injectable, signal} from '@angular/core';
import {catchError, shareReplay, startWith, switchMap, switchMapTo, take, tap} from 'rxjs/operators';
import {BehaviorSubject, from, Observable, of, Subject, throwError} from 'rxjs';
import {
  NgxPlaidLinkService,
  PlaidErrorMetadata,
  PlaidErrorObject,
  PlaidLinkHandler,
  PlaidSuccessMetadata
} from 'ngx-plaid-link';
import {LegacyPlaidConfig, PlaidEventMetadata} from 'ngx-plaid-link/lib/interfaces';
import {ActivatedRoute, Router} from '@angular/router';

import {PlaidDataService} from './plaid-data.service';
import {AchDebitCreateDto} from '../../shared/models/cre-banking/plaid/AchDebitCreateDto.model';
import {AccessTokenCreateDto} from '../../shared/models/cre-banking/plaid/AccessTokenCreateDto.model';
import {StorageService} from '../storage.service';
import {PlaidOAuth} from '../../shared/models/OAuth/PlaidOAuth.model';
import {ExternalClientBankAccountDto} from '../../dashboard/bankAccounts/models/externalClientBankAccountDto.model';
import {TerraUtils} from '../../shared/TerraUtils';
import {AppSettingsService} from '../app-settings.service';
import {OnDestroyMixin} from '@w11k/ngx-componentdestroyed';
import {BaseResponseDto} from 'src/app/shared/models/BaseResponseDto.model';
import {UtilsService} from '../utils.service';
import {LoggerService} from 'src/app/shared/errors/logger.service';
import {BankAccountSyncDetailsDto} from 'src/app/dashboard/bankAccounts/models/bankAccountSyncDetailsDto.model';
import {SnackbarMessageComponent, SnackbarMessageParams} from 'src/app/shared/components/snackbar-message/snackbar-message.component';
import {SnackbarService} from '../snackbar.service';
import {DialogService} from '../dialog.service';
import {AlertDialogParams} from 'src/app/shared/components/alert-dialog/alert-dialog.component';

@Injectable()
export class PlaidService extends OnDestroyMixin {
  private _refreshBankAccounts = new Subject<void>();
  private _isRefreshingLinkedBankAccounts$ = new BehaviorSubject<boolean>(false);
  private _isRefreshingLinkToken$ = new BehaviorSubject<boolean>(false);

  readonly plaidKnowErrors = new Set(['OAUTH_STATE_ID_ALREADY_PROCESSED']);
  readonly openOAuthEventName = 'OPEN_OAUTH';

  isRefreshingLinkedBankAccounts$ = this._isRefreshingLinkedBankAccounts$.asObservable();
  isRefreshingLinkToken$ = this._isRefreshingLinkToken$.asObservable();
  lastSuccessfulAuthAccount = signal('');
  continueAuthProcess = signal(false);

  readonly autoVerificationPendingStatus = 1;
  readonly manualVerificationPendingStatus = 2;

  constructor(private plaidDataService: PlaidDataService,
    private plaidLinkService: NgxPlaidLinkService,
    private storageService: StorageService,
    private route: ActivatedRoute,
    private router: Router,
    private appSettingsService: AppSettingsService,
    private utilsService: UtilsService,
    private snackbarService: SnackbarService,
    private logger: LoggerService,
    private dialogService: DialogService) {
    super();
  }

  public getLinkedPlaidBankAccounts(holdingId?: number, retrieveBalance?: boolean, unitAccountId?: number): Observable<ExternalClientBankAccountDto[]> {
    return this._refreshBankAccounts.pipe(startWith(''))
      .pipe(
        switchMapTo(this.plaidDataService.getLinkedBankAccounts(holdingId, retrieveBalance, unitAccountId)),
        tap(_ => this._isRefreshingLinkedBankAccounts$.next(false)),
        catchError(err => {
          this._isRefreshingLinkedBankAccounts$.next(false);
          return throwError(err);
        }),
        shareReplay(1)
      );
  }

  public getLinkedPlaidBankAccountBalance(account: ExternalClientBankAccountDto): Observable<number> {
    return this.plaidDataService.getLinkedBankAccountBalance(account.externalAccountId, account.accessTokenId);
  }

  /**
   * Exchange public token with access token
   * @param requestData request data for exchanging the public token
   */
  public exchangePublicToken(requestData: AccessTokenCreateDto): Observable<BankAccountSyncDetailsDto | boolean> {
    if (!requestData) {
      of(false);
    }

    return this.plaidDataService.exchangeToken(requestData);
  }

  /**
   * Update linked bank accounts list
   */
  public refreshLinkedBankAccounts() {
    this._refreshBankAccounts.next();
    this._isRefreshingLinkedBankAccounts$.next(true);
  }

  /**
   * Transfer funds from linked Plaid bank account to Unit bank account
   * @param transferUsingPlaidCreateDto Transfer request data
   */
  public transfer(transferUsingPlaidCreateDto: AchDebitCreateDto) {
    if (!transferUsingPlaidCreateDto) {
      return of(null);
    }

    return this.plaidDataService.transferFromLinkedPlaidBankAccount(transferUsingPlaidCreateDto);
  }

  public deleteAccessToken(accessTokenId: string): Observable<any> {
    return this.plaidDataService.deleteAccessToken(accessTokenId).pipe(
      tap(() => {
        this.refreshLinkedBankAccounts();
        this.snackbarService.showSnackbarFromComponent(SnackbarMessageComponent,
          new SnackbarMessageParams(`deleted successfully`));
      }),
      catchError(() => {
        this.snackbarService.showSnackbarFromComponent(SnackbarMessageComponent,
          new SnackbarMessageParams(`Failed To Delete`));
        return of(null);
      })
    );
  }

  /**
   * Creates an observable for Plaid link handler
   * @param linkToken Link token
   * @param onSuccess Callback to execute on successful link
   * @param onExit Callback to execute on link exit
   * @param onEvent Callback to execute on link event
   * @param receivedRedirectUri Redirect URI from OAuth
   */
  public getPlaidLinkHandler(linkToken: string, onSuccess: (publicToken: string, metadata: PlaidSuccessMetadata) => any,
    onExit: (error: PlaidErrorObject, metadata: PlaidErrorMetadata) => any,
    onEvent: (eventName: string, metadata: PlaidEventMetadata) => any,
    receivedRedirectUri: string = null): Observable<PlaidLinkHandler> {
    let plaidConfig: LegacyPlaidConfig = {
      apiVersion: 'v2',
      env: this.appSettingsService.plaidEnvironment,
      token: linkToken,
      product: ['auth'],
      onSuccess,
      onExit,
      onEvent
    };
    if (!!receivedRedirectUri) {
      plaidConfig = Object.assign({}, plaidConfig, {receivedRedirectUri});
    }

    const plaidHandlerPromise = this.plaidLinkService.createPlaid(plaidConfig);

    return from(plaidHandlerPromise);
  }

  /**
   * Get saved data related to OAuth flow
   * @param deleteOAuthInformation To delete the saved OAuth data
   */
  public getOAuthInformation(deleteOAuthInformation: boolean = true): PlaidOAuth {
    const data = this.storageService.getPersistent<PlaidOAuth>(TerraUtils.consts.OAUTH_LOCAL_STORAGE_KEY);
    if (deleteOAuthInformation) {
      this.storageService.deletePersistent(TerraUtils.consts.OAUTH_LOCAL_STORAGE_KEY);
    }

    return data;
  }

  /**
   * Continues Plaid OAuth flow ikf needed
   * @param continueOAuthFlowCallback Callback to execute on successful OAuth flow
   */
  public handlePlaidOAuthRedirect(continueOAuthFlowCallback: (boolean) => void) {
    this.route.queryParams.pipe(take(1)).subscribe(params => {
      if (!!params[TerraUtils.consts.PLAID_OAUTH_REDIRECT_QUERY_PARAM_NAME]) {
        continueOAuthFlowCallback(true);
        // remove the query param
        const newQueryParams = {...params};
        delete newQueryParams[TerraUtils.consts.PLAID_OAUTH_REDIRECT_QUERY_PARAM_NAME];
        this.router.navigate([], {queryParams: newQueryParams, replaceUrl: true});
      }
    });
  }

  /**
   * Opens Plaid link GUI
   */
  public openPlaidLink(accessTokenId: string | null = null) {
    this._isRefreshingLinkToken$.next(true);
    if (accessTokenId) {
      this.continueAuthProcess.set(true);
    }
    this.plaidDataService.getLinkToken(null, accessTokenId).pipe(
      take(1),
      switchMap(linkTokenObject => this.getPlaidLinkHandler(linkTokenObject.linkToken,
        this.onPlaidLinkSuccess.bind(this),
        this.onPlaidLinkExit.bind(this),
        this.onPlaidLinkEvent.bind(this))),
      tap(_ => this._isRefreshingLinkToken$.next(false)),
      catchError(err => {
        this._isRefreshingLinkToken$.next(false);
        return throwError(() => err)
      })
    ).subscribe(handler => {
      handler.open();
    });
  }

  /**
   * Callback to be used when the user successfully linked a bank account using Plaid
   * @param publicToken Public token received from plaid
   * @param metadata Additional data
   */
  private onPlaidLinkSuccess(publicToken: string, metadata: PlaidSuccessMetadata) {
    const exchangeRequestData = new AccessTokenCreateDto();
    exchangeRequestData.publicToken = publicToken;
    exchangeRequestData.verificationStatus = metadata.account.verification_status;
    this.exchangePublicToken(exchangeRequestData).pipe(take(1)).subscribe({
      next: res => {
        this.refreshLinkedBankAccounts();
        if (typeof res === 'object' && res.addedSuccessfully > 0) {
          this.snackbarService.showSnackbarFromComponent(SnackbarMessageComponent,
            new SnackbarMessageParams(`${res.addedSuccessfully} external accounts successfully added`));
          this.handleAuthAccountIfNeeded(res.addedSuccessfully, metadata);
        }
      },
      error: error => {
        if (error instanceof BaseResponseDto) {
          this.utilsService.alertErrorMessage(error);
        } else {
          this.logger.error('Error exchanging public token', error);
        }
      }
    });
  }

  handleAuthAccountIfNeeded(numOfAccountsAdded: number, metadata: PlaidSuccessMetadata) {
    if (numOfAccountsAdded != 1) {
      return;
    }
    if (metadata.account.verification_status?.startsWith('pending')) {
      return;
    }
    this.lastSuccessfulAuthAccount.set(metadata.account_id);
  }

  /**
   * Callback to be used when the user exists Plaid link
   * @param error Error if exists
   * @param metadata Additional data
   */
  private onPlaidLinkExit(error: PlaidErrorObject, metadata: PlaidErrorMetadata) {
    if (!!error) {
      if (!this.plaidKnowErrors.has(error.error_code)) {
        this.logger.error('Plaid link closed with an error', error, metadata);
      }
      if (this.continueAuthProcess()) {
        this.dialogService.alertDialog({
          title: 'Account Linking Failed - Please Retry',
          description: `Plaid could not complete the connection for this account. Please try linking your account again. ${(error.display_message ? 'ERROR: ' : '')} ${(error.display_message ? error.display_message : '')}`
        } as AlertDialogParams).afterClosed()
          .subscribe(_ => this.openPlaidLink());
        this.continueAuthProcess.set(false);
        return;
      }
      // need to do the link process again in case of an error
      // this.data.continueOAuthFlow = false;
      this.openPlaidLink();
    }
  }

  /**
   * Callback to be used on event from plaid link
   * @param eventName Name of the event
   * @param metadata Additional data
   */
  private onPlaidLinkEvent(eventName: string, metadata: PlaidEventMetadata) {
    if (eventName === this.openOAuthEventName) {
      // this.saveOAuthInformation(0, this.paymentToken);
    }
  }
}
